import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import { ProductSalesByLocationMachine } from '../../services/openapi';
import { buildProductSalesReport, formatAmount, formatQuantity, LocationGroup } from './productSalesGrouping';

export type ProductSalesPdfLabels = {
    title: string;
    startDate: string;
    endDate: string;
    generatedOn: string;
    product: string;
    quantity: string;
    amount: string;
    subtotal: string;
    total: string;
    totalsPerProduct: string;
    grandTotal: string;
    site: string;
    unknown: string;
};

// Matches the colors used for the on-screen report (see sales-report-table rules in _ui-kit.scss).
const HEADER_COLOR: [number, number, number] = [43, 46, 131];
const MACHINE_COLOR: [number, number, number] = [136, 144, 201];
const SUBTOTAL_COLOR: [number, number, number] = [242, 243, 250];
const LOCATION_TOTAL_COLOR: [number, number, number] = [207, 213, 234];
const WHITE: [number, number, number] = [255, 255, 255];

const PAGE_MARGIN = 40;
const COLUMN_STYLES = { 1: { halign: 'right' as const, cellWidth: 110 }, 2: { halign: 'right' as const, cellWidth: 110 } };
const SITE_GAP = 14;

const buildLocationRows = (location: LocationGroup, showSiteId: boolean, labels: ProductSalesPdfLabels): RowInput[] => {
    const body: RowInput[] = [];

    const locationLabel = showSiteId
        ? `${location.locationDescription}   —   ${labels.site}: ${location.siteLabel}`
        : location.locationDescription;

    body.push([{ content: locationLabel, colSpan: 3, styles: { fillColor: HEADER_COLOR, textColor: WHITE, fontStyle: 'bold' } }]);

    location.machines.forEach((machine) => {
        body.push([{ content: machine.machineDescription, colSpan: 3, styles: { fillColor: MACHINE_COLOR, textColor: WHITE, fontStyle: 'bold' } }]);

        machine.rows.forEach((row) => {
            body.push([
                row.productDescription ?? labels.unknown,
                formatQuantity(row.totalQuantity),
                formatAmount(row.totalAmountInCents)
            ]);
        });

        body.push([
            { content: `${labels.subtotal} ${machine.machineDescription}`, styles: { fontStyle: 'italic', fillColor: SUBTOTAL_COLOR } },
            { content: formatQuantity(machine.totalQuantity), styles: { fontStyle: 'italic', fillColor: SUBTOTAL_COLOR } },
            { content: formatAmount(machine.totalAmountInCents), styles: { fontStyle: 'italic', fillColor: SUBTOTAL_COLOR } }
        ]);
    });

    body.push([
        { content: `${labels.total} ${location.locationDescription}`, styles: { fontStyle: 'bold', fillColor: LOCATION_TOTAL_COLOR } },
        { content: formatQuantity(location.totalQuantity), styles: { fontStyle: 'bold', fillColor: LOCATION_TOTAL_COLOR } },
        { content: formatAmount(location.totalAmountInCents), styles: { fontStyle: 'bold', fillColor: LOCATION_TOTAL_COLOR } }
    ]);

    return body;
};

// yyyy-MM-dd (as the date input gives it) to d-M-yyyy, like the generated-on date. Parsed by hand to avoid time zone shifts.
const formatDate = (date: string) => {
    const [year, month, day] = date.split('-').map(Number);
    return `${day}-${month}-${year}`;
};

export const buildProductSalesPdf = (rows: ProductSalesByLocationMachine[], startDate: string, endDate: string, labels: ProductSalesPdfLabels): jsPDF => {
    const { locationGroups, productTotals, grandTotalQuantity, grandTotalAmountInCents, showSiteId } = buildProductSalesReport(rows, labels.unknown);

    const doc = new jsPDF({ unit: 'pt' });

    // The title and dates go at the top of every page; the tables start below them.
    const headerLines = [
        startDate ? `${labels.startDate}: ${formatDate(startDate)}` : null,
        endDate ? `${labels.endDate}: ${formatDate(endDate)}` : null,
        `${labels.generatedOn}: ${new Date().toLocaleString('nl-NL')}`
    ].filter((line): line is string => line !== null);
    const contentTop = 56 + (headerLines.length - 1) * 12 + 14;

    const drawHeader = () => {
        doc.setFontSize(14);
        doc.text(labels.title, 40, 40);
        doc.setFontSize(9);
        doc.setTextColor(110, 110, 110);
        headerLines.forEach((line, index) => doc.text(line, 40, 56 + index * 12));
        doc.setTextColor(0, 0, 0);
    };

    const tableOptions = {
        theme: 'plain' as const,
        // A location taller than a page continues below the header on the next page.
        margin: { top: contentTop, right: PAGE_MARGIN, bottom: PAGE_MARGIN, left: PAGE_MARGIN },
        styles: { fontSize: 9, cellPadding: 5, lineColor: [220, 220, 220] as [number, number, number], lineWidth: 0.5 },
        headStyles: { fillColor: HEADER_COLOR, textColor: WHITE, fontStyle: 'bold' as const },
        // Fixed widths, so the columns line up from one location's table to the next.
        columnStyles: COLUMN_STYLES
    };
    const head = [[labels.product, labels.quantity, labels.amount]];

    // Height of a table as it would be drawn, measured on a scratch page that is tall enough for it.
    const measureHeight = (body: RowInput[]) => {
        const scratch = new jsPDF({ unit: 'pt', format: [doc.internal.pageSize.getWidth(), 14400] });
        autoTable(scratch, { ...tableOptions, startY: PAGE_MARGIN, showHead: 'never', body });
        return (scratch as any).lastAutoTable.finalY - PAGE_MARGIN;
    };

    // One table per location, drawn right below each other so they read as one table. A location
    // that doesn't fit on the rest of the page starts on a new page, under the column header; only
    // one taller than a whole page is split.
    let y = contentTop;
    let isFirstOnPage = true;

    locationGroups.forEach((location, index) => {
        const body = buildLocationRows(location, showSiteId, labels);
        // Some white space between the sites, except at the top of a page.
        const gap = !isFirstOnPage && showSiteId && index > 0 && location.siteId !== locationGroups[index - 1].siteId ? SITE_GAP : 0;
        let startY = y + gap;

        if (!isFirstOnPage && startY + measureHeight(body) > doc.internal.pageSize.getHeight() - PAGE_MARGIN) {
            doc.addPage();
            startY = contentTop;
            isFirstOnPage = true;
        }

        autoTable(doc, { ...tableOptions, startY, head, showHead: isFirstOnPage ? 'everyPage' : 'never', body });

        y = (doc as any).lastAutoTable.finalY;
        isFirstOnPage = false;
    });

    // The totals per product always start on a page of their own.
    doc.addPage();

    const totalsBody: RowInput[] = productTotals.map((product) => [
        product.productDescription,
        formatQuantity(product.totalQuantity),
        formatAmount(product.totalAmountInCents)
    ]);

    totalsBody.push([
        { content: labels.grandTotal, styles: { fontStyle: 'bold', fillColor: HEADER_COLOR, textColor: WHITE } },
        { content: formatQuantity(grandTotalQuantity), styles: { fontStyle: 'bold', fillColor: HEADER_COLOR, textColor: WHITE } },
        { content: formatAmount(grandTotalAmountInCents), styles: { fontStyle: 'bold', fillColor: HEADER_COLOR, textColor: WHITE } }
    ]);

    autoTable(doc, {
        ...tableOptions,
        startY: contentTop,
        head: [[labels.totalsPerProduct, '', '']],
        body: totalsBody
    });

    for (let page = 1; page <= doc.getNumberOfPages(); page++) {
        doc.setPage(page);
        drawHeader();
    }

    return doc;
};
